import axios from "./axiosInstance";

class AuthApi {
  /**
   * Obtiene datos del usuario actual con manejo mejorado de errores y reintento
   */
  static GetCurrentUser = async () => {
    try {
      // Verificar si tenemos un token válido
      const token = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user")).token
        : null;

      // Si no hay token disponible y tenemos sesión de Clerk, intentar obtener uno nuevo
      if ((!token || axios.isTokenExpiringSoon()) && window.Clerk?.session) {
        await axios.refreshToken();
      }

      // Realizar la petición con el token renovado
      return await axios.get("/users/me");
    } catch (error) {
      console.error("Error en GetCurrentUser:", error);

      // Si es un error de autenticación, intentar renovar el token una vez
      if (error.response?.status === 401 && window.Clerk?.session) {
        try {
          await axios.refreshToken();
          return await axios.get("/users/me");
        } catch (refreshError) {
          console.error("Error renovando token en GetCurrentUser:", refreshError);
          throw refreshError;
        }
      }

      throw error;
    }
  };

  /**
   * Obtiene un usuario por su email
   */
  static GetUserByEmail = async (email) => {
    try {
      return await axios.get(`/api/users/by-email/${email}`);
    } catch (error) {
      // Si es un error de autenticación, intentar renovar el token una vez
      if (error.response?.status === 401 && window.Clerk?.session) {
        try {
          await axios.refreshToken();
          return await axios.get(`/api/users/by-email/${email}`);
        } catch (refreshError) {
          console.error("Error renovando token en GetUserByEmail:", refreshError);
          throw refreshError;
        }
      }
      throw error;
    }
  };

  /**
   * Verifica el estado de un usuario en el sistema con mejor manejo de errores
   */
  static VerifyUserStatus = async (email) => {
    try {
      // Configuración para manejo especial de esta ruta
      const config = {
        skipAuth: false,
        retry: true,
        // Aumentar timeout para esta operación crítica
        timeout: 15000,
      };

      const response = await axios.post("/auth/verify-user", { email }, config);
      return response.data;
    } catch (error) {
      // Manejar respuestas específicas de manera clara
      if (error.response) {
        // Usuario inactivo
        if (error.response.status === 403 && error.response.data.code === "INACTIVE_USER") {
          throw new Error("Usuario inactivo");
        }

        // Usuario no encontrado, no es un error crítico
        if (error.response.status === 404) {
          return { success: true };
        }

        // Error de autenticación, intentar una vez con token renovado
        if (error.response.status === 401 && !error.config?.retry && window.Clerk?.session) {
          try {
            // Renovar token usando la función de axios
            await axios.refreshToken();

            // Reintentar con indicador para evitar recursión
            const retryConfig = {
              retry: true,
              timeout: 15000,
            };
            const retryResponse = await axios.post("/auth/verify-user", { email }, retryConfig);
            return retryResponse.data;
          } catch (retryError) {
            console.error("Error en retry de VerifyUserStatus:", retryError);
            throw retryError;
          }
        }
      }

      // Propagar otros errores
      throw error;
    }
  };

  /**
   * Registra un nuevo usuario con mejor manejo de errores
   */
  static Register = async (userData) => {
    try {
      // Asegurar token válido
      if (window.Clerk?.session) {
        await axios.refreshToken();
      }

      const response = await axios.post("/auth/users/create", userData);
      return response;
    } catch (error) {
      // Manejar error de autenticación
      if (error.response?.status === 401 && window.Clerk?.session) {
        try {
          await axios.refreshToken();
          return await axios.post("/auth/users/create", userData);
        } catch (refreshError) {
          console.error("Error renovando token en Register:", refreshError);
          throw {
            message: "Error de autenticación al registrar usuario",
            error: refreshError,
          };
        }
      }

      // Construir mensaje de error detallado
      if (error.response) {
        throw {
          message: error.response.data.error || "Error en el registro",
          response: error.response,
        };
      } else if (error.request) {
        throw {
          message: "No se recibió respuesta del servidor",
          request: error.request,
        };
      } else {
        throw {
          message: "Error al configurar la solicitud",
          error: error.message,
        };
      }
    }
  };

  /**
   * Cambia la contraseña del usuario actual con mejor manejo de errores
   */
  static ChangePassword = async (oldPassword, newPassword) => {
    try {
      // Asegurar token válido
      if (window.Clerk?.session) {
        await axios.refreshToken();
      }

      const response = await axios.post("/auth/change-password", {
        oldPassword,
        newPassword,
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || "Contraseña actualizada exitosamente",
        };
      } else {
        throw new Error(response.data.error || "Error al cambiar la contraseña");
      }
    } catch (error) {
      // Manejar error de autenticación
      if (error.response?.status === 401 && window.Clerk?.session) {
        try {
          await axios.refreshToken();
          const retryResponse = await axios.post("/auth/change-password", {
            oldPassword,
            newPassword,
          });

          if (retryResponse.data.success) {
            return {
              success: true,
              message: retryResponse.data.message || "Contraseña actualizada exitosamente",
            };
          }
          throw new Error(retryResponse.data.error || "Error al cambiar la contraseña");
        } catch (refreshError) {
          console.error("Error renovando token en ChangePassword:", refreshError);
          throw new Error("Error de autenticación al cambiar contraseña");
        }
      }

      console.error("Error cambiando contraseña:", error);

      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error("Error al cambiar la contraseña");
    }
  };

  /**
   * Cierra la sesión del usuario con mejor manejo de errores
   */
  static Logout = async () => {
    try {
      // Intentar hacer logout en el backend
      try {
        await axios.post("/auth/logout");
      } catch (logoutError) {
        console.warn("Error en logout del backend:", logoutError);
        // Continuar con el logout local aunque falle el backend
      }

      // Cerrar sesión de Clerk
      if (window.Clerk?.session) {
        try {
          await window.Clerk.session.end();
        } catch (clerkError) {
          console.warn("Error en logout de Clerk:", clerkError);
        }
      }

      // Limpiar datos locales
      localStorage.removeItem("user");
      delete axios.defaults.headers.common["Authorization"];

      return true;
    } catch (error) {
      console.error("Error durante el logout:", error);
      // Forzar limpieza local aunque haya errores
      localStorage.removeItem("user");
      delete axios.defaults.headers.common["Authorization"];
      return true;
    }
  };

  /**
   * Método de utilidad para verificar y renovar el token si es necesario
   * con mejor control de errores y caché
   */
  static EnsureValidToken = async (forceRefresh = false) => {
    if (!window.Clerk?.session) {
      return false;
    }

    try {
      // Si no forzamos renovación, verificar si el token actual sigue siendo válido
      if (!forceRefresh) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const { token } = JSON.parse(storedUser);
          if (token && !axios.isTokenExpiringSoon()) {
            return true;
          }
        }
      }

      // Renovar token usando la función de axios
      const token = await axios.refreshToken();

      return !!token;
    } catch (error) {
      console.error("Error renovando token:", error);

      // Emitir evento de sesión expirada
      const event = new CustomEvent("auth:sessionExpired", { detail: { error } });
      window.dispatchEvent(event);

      return false;
    }
  };
}

export default AuthApi;
